import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { TranslocoPipe } from '@jsverse/transloco';
import { PersonalDataAgreement } from '@shared/interfaces';
import { PersonalDataAgreementService } from '@services/api/internal/personal-data-agreement.service';
import { DataAttributeDirective } from '@shared/directives/data-attribute.directive';
import { ConsentStorageService } from '@services/utils/consent-storage.service';
import { UserFacade } from '../../../store/user/user.facade';
import { take } from 'rxjs';

@Component({
  selector: 'tiss-consent-dialog',
  imports: [ButtonModule, TranslocoPipe, DataAttributeDirective],
  templateUrl: './consent-dialog.component.html',
  styleUrl: './consent-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentDialogComponent {
  private ref: DynamicDialogRef = inject(DynamicDialogRef);
  private config: DynamicDialogConfig = inject(DynamicDialogConfig);
  private personalDataAgreementService: PersonalDataAgreementService = inject(PersonalDataAgreementService);
  private consentStorageService: ConsentStorageService = inject(ConsentStorageService);
  private userFacade: UserFacade = inject(UserFacade);

  private readonly userId = computed(() => this.userFacade.$user()?.userId ?? '');

  get agreement(): PersonalDataAgreement | null {
    return this.config.data?.agreement || null;
  }

  close(): void {
    const agreement = this.agreement;
    if (agreement) {
      this.personalDataAgreementService
        .signPersonalDataAgreement(agreement.key, false)
        .pipe(take(1))
        .subscribe();
    }

    this.ref.close(false);
  }

  accept(): void {
    const agreement = this.agreement;
    const userId = this.userId();
    
    if (!agreement || !userId) {
      return;
    }

    this.consentStorageService.addAcceptedKey(userId, agreement.key);

    this.personalDataAgreementService
      .savePersonalDataAgreement(agreement.key)
      .pipe(take(1))
      .subscribe();

    this.personalDataAgreementService
      .signPersonalDataAgreement(agreement.key, true)
      .pipe(take(1))
      .subscribe();

    this.ref.close(true);
  }
}
